import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of, throwError } from 'rxjs';
import { AppSettings } from '../app-settings';
import { CommonService } from './common.service';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class BondsServices {

  constructor(private http: HttpClient) { }

  getBonds(idBusiness: any, page: number, searchText: string, sortFilter: any, places: any | any[]) {
    let url = `${AppSettings.API_URI_V2}/companies/${idBusiness}/discounts?rule_type=pass`;
    if (searchText) {
      url += `&search=${searchText}`;
    }
    if (sortFilter) {
      url += `&sort=${sortFilter.sortedBy}:${sortFilter.sort}`;
    }
    return this.http.get<any>(url).pipe(
      map((data) => {
        return data
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  createBond(payload: any) {
    let url = `${AppSettings.API_URI_V2}/discounts`;
    return this.http.post<any>(url, payload).pipe(
      map((data) => {
        return data
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  editBond(idBond: any, payload: any) {
    let url = `${AppSettings.API_URI_V2}/discounts/${idBond}`;
    return this.http.put<any>(url, payload).pipe(
      map((data) => {
        return data
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  deleteBond(idBond: any) {
    const url = `${AppSettings.API_URI_V2}/discounts/${idBond}`;
    return this.http.delete<any>(url).pipe(
      map((data) => {
        return data
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  toggleActiveStatus(idBond: any, active: boolean) {
    const url = `${AppSettings.API_URI_V2}/discounts/${idBond}/toggle-active`;
    return this.http.patch<any>(url, {}).pipe(
      map((data) => {
        return data
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  getPassById(idPass: number) {
    const url = `${AppSettings.API_URI_V2}/discounts/${idPass}`;
    return this.http.get<any>(url).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * Mapa de un abono de butaca única: misma forma que el mapa por sesión, con
   * la ocupación unida de todos sus eventos y, en `unavailable_seats`, las
   * butacas que no tienen entrada general en alguno de ellos.
   */
  getPassSeatMap(idPass: number) {
    const url = `${AppSettings.API_URI_V2}/passes/${idPass}/seat-map`;
    return this.http.get<any>(url).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * Desglose por evento (entrada, precio y descuento) de las butacas elegidas.
   * Sirve también de consulta "¿están libres en todo el abono?": si alguna no
   * lo está, responde 409. `eventId` indica en el mapa de qué evento se han
   * elegido; sin él, son butacas del mapa del abono.
   */
  quotePassSeats(idPass: number, seatIds: number[], eventId: number | null = null) {
    const url = `${AppSettings.API_URI_V2}/passes/${idPass}/seat-quote`;
    return this.http.post<any>(url, { seat_ids: seatIds, event_id: eventId }).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * Rejilla eventos × niveles de un abono de precio fijo: una columna por nivel
   * (emparejado entre versiones del mapa) y, por evento, los `level_ids` de cada
   * celda con el precio de su entrada general, que es el tope del precio fijo.
   */
  /** Rejilla del abono de precio fijo: una fila por sesión con sus niveles (y los precios guardados si se pasa el abono) */
  getPassGrid(passItems: { event_id: number; session_ids: number[] }[], idDiscount: number | null = null) {
    const url = `${AppSettings.API_URI_V2}/discounts/fixed-price-grid`;
    return this.http.post<any>(url, { pass_items: passItems, id_discount: idDiscount }).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  getPublicPasses(filters?: { active?: boolean; discount_mode?: string; search?: string; quantity?: number }) {
    let url = `${AppSettings.API_URI_V2}/discounts?rule_type=pass`;
    if (filters) {
      if (filters.active !== undefined) url += `&active=${filters.active ? 1 : 0}`;
      if (filters.discount_mode) url += `&discount_mode=${filters.discount_mode}`;
      if (filters.search) url += `&search=${encodeURIComponent(filters.search)}`;
      if (filters.quantity !== undefined) url += `&quantity=${filters.quantity}`;
    }
    return this.http.get<any>(url).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  getPassesByEvent(eventId: number) {
    const url = `${AppSettings.API_URI_V2}/events/${eventId}/passes`;
    return this.http.get<any>(url).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  getBondsByCompany(companyId: number) {
    const url = `${AppSettings.API_URI_V2}/companies/${companyId}/discounts?rule_type=pass`;
    return this.http.get<any>(url).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  getPublicPassesByCompany(companyId: number) {
    const url = `${AppSettings.API_URI_V2}/companies/${companyId}/passes`;
    return this.http.get<any>(url).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }
}
